import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import ExpandIcon from '@material-ui/icons/ExpandMore';
import CheckIcon from '@material-ui/icons/CheckOutlined';
import TextField from '@material-ui/core/TextField';

import BaseDialog from '../../../components/BaseDialog/BaseDialog';
import FormikTextField from '../../../components/Fields/FormikTextField/FormikTextField';
import TimesField from '../../../components/Fields/TimesField/TimesField';
import LoaderProgress from '../../../components/Loader/LoaderProgress/LoaderProgress';
import ErrorDialog from '../../../components/ErrorDialog/ErrorDialog';
import ActionLink from '../../../components/ActionLink/ActionLink';
import modelAPI from '../../../api/model';
import constants from '../../../constants';

import { Formik } from 'formik';
import { find } from 'lodash-es';
import { object, string } from 'yup';

import 'flatpickr/dist/flatpickr.min.css';
import { withStyles } from '@material-ui/core/styles';
import styles from './styles.js';

import {
  getString,
  iPhoneFriendlyButtonLabel,
} from '../../../helpers/stringHelpers.js';

const stringsComponent = 'contactUs';

const initialFormData = {
  enquiryType: '',
  query: '',
  details: '',
  preferredTimesAM: [],
  preferredTimesPM: [],
  email: '',
  phone: '',
};

const initialState = {
  isLoading: false,
  hasError: false,
  confirmedDialogOpen: false,
  enquiryType: '',
  query: '',
  formData: initialFormData,
  editEmail: false,
  editPhone: false,
};

class ContactForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = initialState;

    this.getEnquiryTypes = this.getEnquiryTypes.bind(this);
    this.getEnquirySubTypes = this.getEnquirySubTypes.bind(this);
    this.handleConfirmedDialogToggle =
      this.handleConfirmedDialogToggle.bind(this);
    this.handleClear = this.handleClear.bind(this);
  }

  getEnquiryTypes() {
    const { enquiryOptions } = this.props;
    const enquiryTypes = [];

    enquiryOptions.forEach((element) => {
      enquiryTypes.push({
        value: element.option,
        label: element.option,
        helpText: element.helpText,
        subOptionsLabel: element.subOptionsLabel,
      });
    });

    return enquiryTypes;
  }

  getEnquirySubTypes() {
    const { enquiryOptions } = this.props;
    const enquiryTypes = [];

    const selectedEnquiry = find(enquiryOptions, {
      option: this.state.enquiryType,
    });

    if (selectedEnquiry) {
      selectedEnquiry.subOptions.forEach((element) => {
        enquiryTypes.push({ value: element.option, label: element.option });
      });
    }

    return enquiryTypes;
  }

  handleConfirmedDialogToggle() {
    const { confirmedDialogOpen } = this.state;
    if (confirmedDialogOpen) {
      // Reset form to initial state if closing dialog.
      this.setState(initialState);
    } else {
      this.setState({
        confirmedDialogOpen: !confirmedDialogOpen,
      });
    }
  }

  handleClear(setFieldValue, name, e) {
    setFieldValue([name], '', true);
    // @TODO: Use refs to set the focus.
    document.getElementsByName(name)[0].focus();
  }

  buildSelectField(
    name,
    label,
    placeholder,
    changeHandler,
    value,
    optionsFunction,
    setFieldValue
  ) {
    const { classes, balance } = this.props;

    const options = optionsFunction();
    const friendlyValue = value
      ? find(options, { value: value }) && find(options, { value: value }).label
      : null;

    const helpText =
      find(options, { value: this.state.enquiryType }) &&
      find(options, { value: this.state.enquiryType }).helpText;

    return (
      <div className={classes.fieldContainer}>
        <Typography className={classes.label}>
          <label htmlFor={name}>{label}</label>
        </Typography>
        <Select
          onChange={(event) => {
            changeHandler(event);
            const value = event.target.value;
            this.setState({ [name]: value });
            // Reset query field if enquiry type changes.
            if (name == 'enquiryType') {
              setFieldValue('query', '', true);
            }
          }}
          value={value}
          input={<OutlinedInput name={name} id={name} labelWidth={0} />}
          displayEmpty
          renderValue={(value) => (value ? friendlyValue : placeholder)}
          onBlur={this.handleFieldChange}
          fullWidth
          MenuProps={{
            classes: {
              paper: classes.selectMenu,
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
          }}
          inputProps={{
            className: value
              ? classes.selectInput
              : `${classes.selectInput} ${classes.selectDisabled}`,
          }}
          IconComponent={ExpandIcon}
        >
          {options.map((option, i) => {
            return (
              <MenuItem key={i} value={option.value}>
                {option.label}
              </MenuItem>
            );
          })}
        </Select>
        {helpText ? (
          <Typography
            className={classes.helpText}
            dangerouslySetInnerHTML={{
              __html: helpText,
            }}
          />
        ) : null}
      </div>
    );
  }

  render() {
    const {
      classes,
      enquiryTypeLabel,
      selectPlaceholder,
      detailsLabel,
      detailsPlaceholder,
      timeFieldIntro,
      submitButtonLabel,
      dialogTitle,
      dialogContent,
      onError,
      failureText,
      gaEvent,
      contactData,
      refreshContacts,
    } = this.props;

    const {
      enquiryType,
      query,
      formData,
      confirmedDialogOpen,
      hasError,
      isLoading,
      editEmail,
      editPhone,
    } = this.state;

    const initialValues = formData;

    // Email or phone is required when customer does not currently have either.
    const contactRequired = !contactData.email && !contactData.preferredPhone;

    const validationSchema = object().shape(
      {
        enquiryType: string().required(),
        // Query is required, if query options are available for the enquiry type.
        query: string().when(['enquiryType'], {
          is: () =>
            this.state.enquiryType && this.getEnquirySubTypes().length > 0,
          then: string().required(),
        }),
        details: string().required(''),
        // Preferred time is required - either AM or PM (or both)
        preferredTimesAM: string().when(['preferredTimesPM'], {
          is: (preferredTimesPM) => preferredTimesPM.length == 0,
          then: string().required(),
        }),
        email:
          editEmail || contactRequired
            ? string().when(['phone'], {
                is: (phone) => phone && !editEmail,
                then: string(),
                otherwise: string()
                  .required()
                  .matches(constants.EMAIL_REGEX, ''),
              })
            : string(),
        phone:
          editPhone || contactRequired
            ? string().when(['email'], {
                is: (email) => email && !editPhone,
                then: string(),
                otherwise: string()
                  .required()
                  .matches(constants.PHONE_REGEX, ''),
              })
            : string(),
      },
      ['phone', 'email']
    );

    const preferredTimes = [
      { id: 'Monday', name: 'Monday' },
      { id: 'Tuesday', name: 'Tuesday' },
      { id: 'Wednesday', name: 'Wednesday' },
      { id: 'Thursday', name: 'Thursday' },
      { id: 'Friday', name: 'Friday' },
    ];

    return (
      <React.Fragment>
        <LoaderProgress show={isLoading} />
        {hasError && <ErrorDialog logout={onError} text={failureText} />}
        <BaseDialog
          content={<div className={classes.dialogContent}>{dialogContent}</div>}
          title={dialogTitle}
          buttonLabel={getString('contactUs', 'CLOSE_LABEL')}
          buttonAction={() => {
            this.handleConfirmedDialogToggle();
            // Refresh contacts after closing confirmation dialog, as contact
            // information may have been updated.
            refreshContacts();
          }}
          icon={CheckIcon}
          largeTitle={true}
          inlineTitle={true}
          open={confirmedDialogOpen}
          isHTML={true}
          contentWrap={false}
        />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => {
            this.setState({ formData: values, isLoading: true });
            modelAPI
              .addContactUs(values)
              .then((response) => {
                this.handleConfirmedDialogToggle();
                this.setState({ isLoading: false }, () => {
                  resetForm(initialFormData);
                });
                gaEvent('Contact', 'Click', 'Submit form');
                gaEvent(
                  'Contact',
                  'Submit',
                  `Enquiry Type: ${values.enquiryType}`
                );
                const query = values.query;
                if (query.length) {
                  gaEvent('Contact', 'Submit', `Query: ${values.query}`);
                }
              })
              .catch((err) => {
                this.setState({ isLoading: false, hasError: true });
              });
          }}
        >
          {(props) => {
            const {
              handleChange,
              handleSubmit,
              setFieldValue,
              isValid,
              values,
              errors,
            } = props;

            const submitDisabled = !isValid || !values.details.length;

            return (
              <form>
                <input
                  type="hidden"
                  name="accountType"
                  value={values.accountType}
                />
                <input
                  type="hidden"
                  name="paymentRef"
                  value={values.paymentRef}
                />
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sm={10}
                    md={12}
                    lg={7}
                    className={classes.fieldsContainer}
                  >
                    {this.buildSelectField(
                      'enquiryType',
                      enquiryTypeLabel,
                      selectPlaceholder,
                      handleChange,
                      values.enquiryType,
                      this.getEnquiryTypes,
                      setFieldValue
                    )}
                    {enquiryType && this.getEnquirySubTypes().length
                      ? this.buildSelectField(
                          'query',
                          find(this.getEnquiryTypes(), {
                            value: enquiryType,
                          }).subOptionsLabel,
                          selectPlaceholder,
                          handleChange,
                          values.query,
                          this.getEnquirySubTypes,
                          setFieldValue
                        )
                      : null}
                    {enquiryType &&
                    (!this.getEnquirySubTypes().length || query) ? (
                      <React.Fragment>
                        <Typography
                          className={`${classes.label} ${classes.textFieldLabel}`}
                        >
                          {detailsLabel}
                        </Typography>
                        <FormikTextField
                          name="details"
                          value={values.details}
                          label={detailsPlaceholder}
                          clear={(e) => {
                            this.handleClear(setFieldValue, 'details', e);
                          }}
                          className={classes.jobCommentsInput}
                          classes={{
                            label: classes.jobCommentsInput,
                            input: classes.jobCommentsInput,
                          }}
                        />
                      </React.Fragment>
                    ) : null}
                    {values.details ? (
                      <React.Fragment>
                        <div>
                          {contactRequired && (
                            <React.Fragment>
                              <Typography className={classes.contactHelpText}>
                                {getString(stringsComponent, 'CONTACT_HELP')}
                              </Typography>
                            </React.Fragment>
                          )}
                          <Typography className={classes.label}>
                            {getString(
                              stringsComponent,
                              'CONTACT_DETAILS_HEADING'
                            )}
                          </Typography>
                          <Typography>
                            {getString(
                              stringsComponent,
                              'CONTACT_DETAILS_TEXT'
                            )}
                          </Typography>
                          <Typography className={`${classes.label}`}>
                            {getString(stringsComponent, 'EMAIL_LABEL')}
                          </Typography>
                          <Typography>
                            {contactData.email
                              ? contactData.email
                              : getString(
                                  stringsComponent,
                                  'EMAIL_EMPTY_VALUE'
                                )}
                            &nbsp;|&nbsp;
                            <ActionLink
                              label={getString(
                                stringsComponent,
                                'UPDATE_CONTACT_LABEL'
                              )}
                              action={() => {
                                this.setState({ editEmail: !editEmail });
                                setFieldValue('email', '', true);
                              }}
                            />
                          </Typography>
                          {editEmail && (
                            <React.Fragment>
                              <Typography className={`${classes.label}`}>
                                {getString(
                                  stringsComponent,
                                  'EMAIL_FIELD_LABEL'
                                )}
                              </Typography>
                              <TextField
                                variant="outlined"
                                id="email"
                                name="email"
                                value={values.email}
                                onChange={handleChange}
                                placeholder={getString(
                                  stringsComponent,
                                  'EMAIL_PLACEHOLDER'
                                )}
                                inputProps={{
                                  className: classes.textInput,
                                }}
                                fullWidth
                                error={
                                  values.email != '' && Boolean(errors.email)
                                }
                                helperText={
                                  values.email != '' &&
                                  Boolean(errors.email) &&
                                  getString(
                                    stringsComponent,
                                    'EMAIL_FIELD_ERROR'
                                  )
                                }
                              />
                            </React.Fragment>
                          )}
                        </div>
                        <div>
                          <Typography className={`${classes.label}`}>
                            {getString(stringsComponent, 'PHONE_LABEL')}
                          </Typography>
                          <Typography>
                            {contactData.preferredPhone
                              ? contactData.preferredPhone
                              : getString(
                                  stringsComponent,
                                  'PHONE_EMPTY_VALUE'
                                )}
                            &nbsp;|&nbsp;
                            <ActionLink
                              label={getString(
                                stringsComponent,
                                'UPDATE_CONTACT_LABEL'
                              )}
                              action={() => {
                                this.setState({ editPhone: !editPhone });
                                setFieldValue('phone', '', true);
                              }}
                            />
                          </Typography>
                          {editPhone && (
                            <React.Fragment>
                              <Typography className={`${classes.label}`}>
                                {getString(
                                  stringsComponent,
                                  'PHONE_FIELD_LABEL'
                                )}
                              </Typography>
                              <TextField
                                variant="outlined"
                                id="phone"
                                name="phone"
                                value={values.phone}
                                onChange={handleChange}
                                placeholder={getString(
                                  stringsComponent,
                                  'PHONE_PLACEHOLDER'
                                )}
                                inputProps={{
                                  className: classes.textInput,
                                }}
                                fullWidth
                                error={
                                  values.phone != '' && Boolean(errors.phone)
                                }
                                helperText={
                                  values.phone != '' &&
                                  Boolean(errors.phone) &&
                                  getString(
                                    stringsComponent,
                                    'PHONE_FIELD_ERROR'
                                  )
                                }
                              />
                            </React.Fragment>
                          )}
                        </div>
                        <Divider className={classes.divider} />
                        {timeFieldIntro ? (
                          <Typography
                            component="div"
                            className={classes.timeFieldIntroContainer}
                          >
                            {timeFieldIntro}
                          </Typography>
                        ) : null}
                        <div className={classes.timeFieldContainer}>
                          <Grid container className={classes.timeFieldHeader}>
                            <Grid item xs={2} />
                            <Grid
                              item
                              xs={2}
                              className={classes.availabilityColumn}
                            >
                              <Typography variant="body1">M</Typography>
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              className={classes.availabilityColumn}
                            >
                              <Typography variant="body1">T</Typography>
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              className={classes.availabilityColumn}
                            >
                              <Typography variant="body1">W</Typography>
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              className={classes.availabilityColumn}
                            >
                              <Typography variant="body1">T</Typography>
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              className={classes.availabilityColumn}
                            >
                              <Typography variant="body1">F</Typography>
                            </Grid>
                          </Grid>
                          <TimesField
                            fieldName="preferredTimesAM"
                            preferredTimes={preferredTimes}
                            value={values.preferredTimesAM}
                            label="AM"
                          />
                          <TimesField
                            fieldName="preferredTimesPM"
                            preferredTimes={preferredTimes}
                            value={values.preferredTimesPM}
                            label="PM"
                          />
                        </div>
                        <Divider className={classes.divider} />
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleSubmit}
                          disabled={submitDisabled}
                          className={classes.buttonSubmit}
                        >
                          {iPhoneFriendlyButtonLabel(
                            submitButtonLabel,
                            submitDisabled
                          )}
                        </Button>
                      </React.Fragment>
                    ) : null}
                  </Grid>
                </Grid>
              </form>
            );
          }}
        </Formik>
      </React.Fragment>
    );
  }
}

ContactForm.propTypes = {
  classes: PropTypes.object.isRequired,
  enquiryOptions: PropTypes.array.isRequired,
  enquiryTypeLabel: PropTypes.string,
  selectPlaceholder: PropTypes.string,
  detailsLabel: PropTypes.string,
  detailsPlaceholder: PropTypes.string,
  timeFieldIntro: PropTypes.object,
  submitButtonLabel: PropTypes.string,
  dialogTitle: PropTypes.string,
  dialogContent: PropTypes.object,
  gaEvent: PropTypes.func.isRequired,
  contactData: PropTypes.object.isRequired,
  refreshContacts: PropTypes.func.isRequired,
};

ContactForm.defaultProps = {
  enquiryTypeLabel: null,
  selectPlaceholder: null,
  detailsLabel: null,
  detailsPlaceholder: null,
  timeFieldIntro: null,
  submitButtonLabel: null,
  dialogTitle: null,
  dialogContent: null,
};

export default withStyles(styles, { withTheme: true })(ContactForm);
